import React from 'react';
import styled from 'styled-components';
import { Flex, Typo, theme } from '../../../../ui';
import { useSettingsObject } from '../../../settings/hooks';
import { getLighterThemeColor, getUserThemeColor, shadeColor, toRgba } from '../../../../ui/theme/colors';
import Icon from '../../../../ui/Icon';

const Card = styled(Flex)`
  background: transparent;
  border: none;
  transition: all 0.2s ease;
`;

const GoalHeader = styled(Flex)`
  margin-bottom: 20px;
`;

const GoalIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${(props) => getLighterThemeColor(props.$brandColor) || theme.colors.PRIMARY};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.$brandColor || theme.colors.PRIMARY};
`;

const ProgressSection = styled(Flex)`
  margin: 20px 0;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: ${theme.colors.NEU_200};
  border-radius: 4px;
  overflow: hidden;
  position: relative;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background: ${(props) => props.$brandColor || theme.colors.PRIMARY};
  border-radius: 4px;
  width: ${(props) => props.$progress}%;
  transition: width 0.6s ease;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent, ${toRgba(theme.colors.NEU_0, 0.3)}, transparent);
    animation: shimmer 2s infinite;
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

const MetricsGrid = styled.div`
  display: ${(props) => (props.$compact ? 'none' : 'grid')};
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  width: 100%;
  margin-top: 20px;
`;

const MetricCard = styled(Flex)`
  background: ${(props) => props.$brandColor};
  border-radius: 6px;
  padding: 8px;
`;

const TrendIndicator = styled(Flex)`
  padding: 4px 8px;
  border-radius: 4px;
  background: ${(props) => {
    if (props.$trend === 'up') return shadeColor(theme.colors.SUCCESS_GREEN, 0.92);
    if (props.$trend === 'down') return shadeColor(theme.colors.ERROR_RED, 0.92);
    return theme.colors.NEU_200;
  }};
  color: ${(props) => {
    if (props.$trend === 'up') return theme.colors.SUCCESS_GREEN;
    if (props.$trend === 'down') return theme.colors.ERROR_RED;
    return theme.colors.NEU_600;
  }};
`;

const DeadlineInfo = styled(Flex)`
  padding: 20px 0;
  border-top: 1px solid ${theme.colors.NEU_200};
  margin-top: 45px;
`;

const getGoalIcon = (type) => {
  switch (type) {
    case 'revenue':
      return 'BarChart';
    case 'creation':
      return 'Course';
    case 'distribution':
      return 'Users';
    default:
      return 'Target';
  }
};

const formatCurrency = (value, currency = '€') => {
  return `${currency}${value.toLocaleString()}`;
};

const formatNumber = (value) => {
  return value.toLocaleString();
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const GoalCard = ({ goal, compact = false }) => {
  const settings = useSettingsObject();
  const userTheme = settings?.theme;
  const brandColor = getUserThemeColor(userTheme?.color);
  const lighterBrandColor = getLighterThemeColor(userTheme?.color);

  const { type, title, subtitle, target, current, currency, unit, deadline, progress, analytics } = goal;

  const progressPercentage = Math.round(progress * 100);
  const IconComponent = Icon[getGoalIcon(type)];

  const formatValue = (value) => {
    if (type === 'revenue') {
      return formatCurrency(value, currency);
    }
    return formatNumber(value);
  };

  const getTrendIcon = () => {
    if (analytics.trend === 'up') return 'TrendingUp';
    if (analytics.trend === 'down') return 'TrendingDown';
    return 'Minus';
  };

  const TrendIcon = Icon[getTrendIcon()];

  return (
    <Card direction='column' $brandColor={brandColor} width='100%'>
      <GoalHeader justify='space-between' align='flex-start' width='100%'>
        <Flex direction='column' gap='8' align='flex-start'>
          <Flex align='center' gap='12'>
            <GoalIcon $brandColor={brandColor}>{IconComponent && <IconComponent size='small' />}</GoalIcon>
            <Typo.ContentSmallHeader>{title}</Typo.ContentSmallHeader>
          </Flex>
          <Typo.SmallText>{subtitle}</Typo.SmallText>
        </Flex>

        <TrendIndicator $trend={analytics.trend} align='center' gap='4'>
          {TrendIcon && <TrendIcon size='x-small' />}
          <Typo.SmallText fontWeight='600'>
            {analytics.trendPercentage > 0 ? '+' : ''}
            {analytics.trendPercentage}%
          </Typo.SmallText>
        </TrendIndicator>
      </GoalHeader>

      <ProgressSection direction='column' gap='12' width='100%'>
        <Flex justify='space-between' align='baseline' gap='20'>
          <Typo.ContentBigHeader color={theme.colors.NEU_PRIMARY}>{formatValue(current)}</Typo.ContentBigHeader>
          <Typo.MediumText>{`of ${formatValue(target)} ${unit || ''}`}</Typo.MediumText>
        </Flex>

        <ProgressBarContainer>
          <ProgressBarFill $progress={progressPercentage} $brandColor={brandColor} />
        </ProgressBarContainer>

        <Flex justify='space-between' align='center' gap='20'>
          <Typo.MediumText fontWeight='600' color={theme.colors.NEU_PRIMARY}>
            {`${progressPercentage}% Complete`}
          </Typo.MediumText>
          <Typo.Info color={theme.colors.NEU_600}>{`${analytics.daysRemaining} days left`}</Typo.Info>
        </Flex>
      </ProgressSection>

      <MetricsGrid $compact={compact}>
        <MetricCard direction='column' gap='4' $brandColor={lighterBrandColor}>
          <Typo.Info color={theme.colors.NEU_600}>Monthly Average</Typo.Info>
          <Typo.MediumText fontWeight='600' color={theme.colors.NEU_PRIMARY}>
            {formatValue(analytics.monthlyAverage)}
          </Typo.MediumText>
        </MetricCard>

        <MetricCard direction='column' gap='4' $brandColor={lighterBrandColor}>
          <Typo.Info color={theme.colors.NEU_600}>Last Month</Typo.Info>
          <Typo.MediumText fontWeight='600' color={theme.colors.NEU_PRIMARY}>
            {type === 'revenue'
              ? formatValue(analytics.lastMonthSales)
              : type === 'creation'
              ? `${analytics.lastMonthCreated} ${unit || ''}`
              : `${analytics.lastMonthEnrollments} ${unit || ''}`}
          </Typo.MediumText>
        </MetricCard>

        <MetricCard direction='column' gap='4' $brandColor={lighterBrandColor}>
          <Typo.Info color={theme.colors.NEU_600}>Estimated Completion</Typo.Info>
          <Typo.MediumText fontWeight='600' color={theme.colors.NEU_PRIMARY}>
            {formatDate(analytics.projectedCompletion)}
          </Typo.MediumText>
        </MetricCard>
      </MetricsGrid>

      <DeadlineInfo align='center' gap='8' width='100%' justify='center'>
        <Icon.Calendar size='x-small' color={theme.colors.NEU_600} />
        <Typo.Info color={theme.colors.NEU_600}>
          Target deadline: <strong>{formatDate(deadline)}</strong>
        </Typo.Info>
      </DeadlineInfo>
    </Card>
  );
};

export default GoalCard;
